import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from 'sonner';
import api from "../lib/api";
import { useAuthStore } from "../store/authStore";

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

export default function LoginPage() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);
  const [loading, setLoading] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const response = await api.post('/auth/login', data);
      setAuth(response.data.user, response.data.access_token);
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <div className="w-full max-w-md bg-surface border border-border/40 rounded-sm p-8">
        <div className="mb-6">
          <h1 className="text-3xl font-black font-heading tracking-tight text-primary">MROD SUPERBOT</h1>
          <p className="text-sm text-text-muted mt-1">Control Center Login</p>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2 text-text-main">Email</label>
            <input
              data-testid="login-email-input"
              type="email"
              {...register('email')}
              className="w-full px-4 py-2 bg-black/50 border border-border focus:border-primary text-text-main rounded-sm"
            />
            {errors.email && <p className="text-destructive text-xs mt-1">{errors.email.message}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium mb-2 text-text-main">Password</label>
            <input
              data-testid="login-password-input"
              type="password"
              {...register('password')}
              className="w-full px-4 py-2 bg-black/50 border border-border focus:border-primary text-text-main rounded-sm"
            />
            {errors.password && <p className="text-destructive text-xs mt-1">{errors.password.message}</p>}
          </div>
          <button
            data-testid="login-submit-button"
            type="submit"
            disabled={loading}
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wider py-2 px-4 rounded-sm"
          >
            {loading ? 'LOGGING IN...' : 'LOGIN'}
          </button>
        </form>
        <div className="mt-4 text-center">
          <p className="text-sm text-text-muted">
            Don't have an account? <Link to="/register" className="text-primary hover:underline">Register</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
